import "./style.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../context/AppContextProvider";
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Menu,
  Modal,
  Popconfirm,
  Popover,
  Result,
  Row,
  Tag,
} from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import QuantityStepper from "../../../components/QuantityStepper";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { useNavigate } from "react-router-dom";
import { getQRproducts, placeQRproductsOrder } from "../../../network/network";
import { useTranslation } from "react-i18next";
import TaxCalculator from "../../../components/TaxCalculator";
import { handleApiError } from "../../../utils/errorHandler";
import {
  QR_LANGUAGES,
  applyQrLanguage,
  qrLangShort,
  readStoredQrLocale,
  restoreQrLanguage,
} from "./qrLanguage";
import {
  clearCartIfMenuChanged,
  clearQrCart,
  getQrMenuProductIds,
  readQrMenuProductIds,
  saveQrCartItems,
  saveQrMenuProductIds,
} from "./qrCartStorage";

const QRcart = () => {
  const { appConfig, setAppConfigWrapper, roundOff } = useContext(AppContext);
  const [totalTax, setTotalTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [cart, setCart] = useState([]);
  const [emptyCart, setEmptyCart] = useState(false);
  const [backLink, setBackLink] = useState("");
  const [orderPlace, setOrderPlace] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const orderSubmittingRef = useRef(false);
  const menuValidationStartedRef = useRef(false);
  const { t, i18n } = useTranslation("qr");
  const navigate = useNavigate();
  const [infoForm] = Form.useForm();
  const setAppConfigWrapperRef = useRef(setAppConfigWrapper);
  const translationRef = useRef(t);
  const totalQuantity = cart.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0,
  );

  translationRef.current = t;

  // Load language from localStorage
  useEffect(() => {
    restoreQrLanguage(setAppConfigWrapperRef.current);
  }, []);

  // -------- Load cart from localStorage --------
  useEffect(() => {
    const stored = localStorage.getItem("cart_items");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const storedMenuProductIds = readQrMenuProductIds();

        if (
          storedMenuProductIds.length &&
          clearCartIfMenuChanged(storedMenuProductIds)
        ) {
          setCart([]);
          return;
        }

        setCart(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error("Invalid cart_items in localStorage", e);
        clearQrCart();
        setCart([]);
      }
    }
  }, []);

  // -------- Validate cart with latest QR menu products --------
  useEffect(() => {
    if (menuValidationStartedRef.current) return;
    menuValidationStartedRef.current = true;

    const src = localStorage.getItem("src");
    const table = localStorage.getItem("table");

    if (!src || !table) {
      return;
    }

    getQRproducts({ src, table })
      .then((response) => {
        const data = response.data || response.response?.data;

        if (data?.success) {
          const menuProductIds = getQrMenuProductIds(
            data.data.categories_products || [],
          );

          saveQrMenuProductIds(menuProductIds);

          if (clearCartIfMenuChanged(menuProductIds)) {
            setCart([]);
          }
        } else {
          handleApiError(response, translationRef.current);
        }
      })
      .catch((error) => handleApiError(error, translationRef.current));
  }, []);

  // -------- Calculate totals & tax whenever cart changes --------
  useEffect(() => {
    let tax = 0;
    let total = 0;

    cart.forEach((product) => {
      const productSubTotal = product.price * product.quantity;
      let productTax = 0;

      if ((product.is_taxable || product.tax > 0) && product.taxes?.length) {
        product.taxes.forEach((tItem) => {
          productTax += productSubTotal * (Number(tItem.rate) / 100);
        });
      }

      tax += productTax;
      total += productSubTotal + productTax;
    });

    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cart, roundOff]);

  useEffect(() => {
    setEmptyCart(cart.length === 0);
  }, [cart]);

  // -------- Build backLink for "back to menu" --------
  useEffect(() => {
    if (
      localStorage.getItem("src") &&
      localStorage.getItem("table") &&
      localStorage.getItem("is_drive_thru")
    ) {
      setBackLink(
        "/qr/ordering?src=" +
          localStorage.getItem("src") +
          "&table=" +
          localStorage.getItem("table") +
          "&is_drive_thru=" +
          localStorage.getItem("is_drive_thru"),
      );
    }
  }, []);

  // -------- Cart operations --------
  const cartReducer = (product, action) => {
    let cartNew = [...cart];

    const indexFound =
      product.cart_key != null
        ? cartNew.findIndex((p) => p.cart_key === product.cart_key)
        : cartNew.findIndex((p) => p.id === product.id);

    if (indexFound === -1) {
      return;
    }

    if (action === "inc") {
      cartNew[indexFound] = {
        ...cartNew[indexFound],
        quantity: cartNew[indexFound].quantity + 1,
      };
    } else if (action === "dec") {
      const newQty = cartNew[indexFound].quantity - 1;
      if (newQty <= 0) {
        cartNew.splice(indexFound, 1);
      } else {
        cartNew[indexFound] = {
          ...cartNew[indexFound],
          quantity: newQty,
        };
      }
    }
    setCart(cartNew);
    saveQrCartItems(cartNew);
  };

  // -------- Place order (normal mode) --------
  const handleCheckout = () => {
    if (orderSubmittingRef.current) return;

    orderSubmittingRef.current = true;
    setLoading(true);

    const transactionObj = {
      src: localStorage.getItem("src"),
      table: localStorage.getItem("table"),
      is_drive_thru: localStorage.getItem("is_drive_thru"),
      items: JSON.stringify(
        cart.map((item) => ({
          id: item.id,
          qty: item.quantity,
          price: item.price,
          tags:
            item.selectedTagIds && item.selectedTagIds.length
              ? item.selectedTagIds
              : [],
        })),
      ),
    };

    placeQRproductsOrder(transactionObj)
      .then((response) => {
        const data = response.data || response.response.data;
        if (data.success === true) {
          setCart([]);
          setOrderPlace(true);
          clearQrCart();
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        orderSubmittingRef.current = false;
        setLoading(false);
      });
  };

  // -------- Additional info modal handlers (drive-thru) --------
  const handleCancel = () => {
    infoForm.resetFields();
    setModalVisible(false);
  };

  const handleAddEdit = () => {
    if (orderSubmittingRef.current) return;

    orderSubmittingRef.current = true;

    infoForm
      .validateFields()
      .then((values) => {
        setLoading(true);

        const transactionObj = {
          customer_name: values.customer_name,
          vehicle_color: values.vehicle_color,
          vehicle_number: values.vehicle_number,
          src: localStorage.getItem("src"),
          table: localStorage.getItem("table"),
          is_drive_thru: localStorage.getItem("is_drive_thru"),
          items: JSON.stringify(
            cart.map((item) => ({
              id: item.id,
              qty: item.quantity,
              price: item.price,
              tags:
                item.selectedTagIds && item.selectedTagIds.length
                  ? item.selectedTagIds
                  : [],
            })),
          ),
        };

        placeQRproductsOrder(transactionObj)
          .then((response) => {
            const data = response.data || response.response.data;
            if (data.success === true) {
              setModalVisible(false);
              setCart([]);
              setOrderPlace(true);
              clearQrCart();
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            orderSubmittingRef.current = false;
            setLoading(false);
          });
      })
      .catch(() => {
        orderSubmittingRef.current = false;
      });
  };

  return (
    <>
      <div
        className="qr-cart-page"
        style={{ direction: appConfig.direction }}
      >
        {/* Header */}
        <header className="qr-cart-header">
          <Button
            type="default"
            className="qr-cart-header__button"
            onClick={() => {
              navigate(backLink || "/");
            }}
            icon={
              appConfig.direction === "rtl" ? (
                <RightOutlined />
              ) : (
                <LeftOutlined />
              )
            }
            aria-label={t("menu")}
          />
          <h1 className="qr-cart-header__title">{t("basket")}</h1>
          <div className="qr-cart-header__actions">
            <Button
              type="primary"
              className="qr-cart-header__button qr-cart-header__language"
              onClick={() => {
                setDrawerVisible(true);
              }}
            >
              {qrLangShort(i18n.language)}
            </Button>
            <Popconfirm
              title={t("warning_empty_basket")}
              onConfirm={() => {
                clearQrCart();
                setCart([]);
              }}
              okText={t("yes")}
              cancelText={t("no")}
              disabled={emptyCart}
              placement="bottomLeft"
            >
              <Button
                type="text"
                danger
                className="qr-cart-header__button"
                icon={<DeleteOutlined />}
                disabled={emptyCart}
                aria-label={t("warning_empty_basket")}
              />
            </Popconfirm>
          </div>
        </header>

        {/* Body */}
        {!orderPlace ? (
          <>
            {!emptyCart ? (
              <div className="qr-cart-content">
                {/* Cart items */}
                <div className="qr-cart-section-heading">
                  <h2>{t("selected_item")}</h2>
                  <span className="qr-cart-item-count">{totalQuantity}</span>
                </div>
                <Row gutter={[12, 12]} className="qr-cart-grid">
                  {cart.map((product) => (
                    <Col
                      xs={24}
                      sm={12}
                      md={12}
                      lg={8}
                      xl={6}
                      xxl={6}
                      key={product.cart_key || product.id}
                    >
                      <article className="qr-cart-item-card">
                        <div className="qr-cart-item-card__layout">
                          <img
                            className="qr-cart-item-card__image"
                            alt={
                              appConfig.direction === "rtl"
                                ? product.name
                                : product.name_en
                            }
                            src={
                              product.image
                                ? product.image
                                : "/images/image-not-found.png"
                            }
                          />
                          <div className="qr-cart-item-card__content">
                            <div className="qr-cart-item-card__name">
                              {appConfig.direction === "rtl"
                                ? product.name
                                : product.name_en}
                            </div>

                            {/* Selected options / tags */}
                            {product.tags &&
                              product.tags.length > 0 &&
                              product.selectedTagIds &&
                              product.selectedTagIds.length > 0 && (
                                <div className="qr-cart-item-card__options">
                                  <div className="qr-cart-item-card__options-label">
                                    {t("options")}:
                                  </div>
                                  <div className="qr-cart-item-card__tags">
                                    {product.selectedTagIds.map((tid) => {
                                      const tagObj = product.tags.find(
                                        (titem) =>
                                          Number(titem.id) === Number(tid),
                                      );
                                      const tagLabel = tagObj
                                        ? appConfig.direction === "rtl"
                                          ? tagObj.name_ar || tagObj.name
                                          : tagObj.name
                                        : tid;
                                      return (
                                        <Tag key={String(tid)}>
                                          {tagLabel}
                                        </Tag>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                            <div className="qr-cart-item-card__footer">
                              <div className="qr-cart-item-card__price">
                                <CurrencyFormatter value={product.price} />
                              </div>
                              <div className="qr-cart-item-card__quantity">
                                <QuantityStepper
                                  className="qr-cart-quantity-stepper"
                                  value={product.quantity}
                                  increaseDisabled={
                                    product.is_stockable &&
                                    Number(product.stock) <=
                                      Number(product.quantity)
                                  }
                                  onDecrease={() => cartReducer(product, "dec")}
                                  onIncrease={() => cartReducer(product, "inc")}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    </Col>
                  ))}
                </Row>

                {/* Totals area */}
                <footer className="qr-cart-checkout">
                  <div className="qr-cart-checkout__inner">
                    <div className="qr-cart-summary">
                      <div className="qr-cart-summary__row">
                        <span>{t("subtotal")}</span>
                        <strong>
                          <CurrencyFormatter
                            value={roundOff(totalAmount - totalTax)}
                          />
                        </strong>
                      </div>
                      <div className="qr-cart-summary__row">
                        <span className="qr-cart-summary__label">
                          {t("taxes")}{" "}
                          <Popover
                            content={<TaxCalculator data={cart} />}
                            trigger="click"
                          >
                            <InfoCircleOutlined className="app-info-icon" />
                          </Popover>
                        </span>
                        <strong>
                          <CurrencyFormatter value={roundOff(totalTax)} />
                        </strong>
                      </div>
                      <div className="qr-cart-summary__row qr-cart-summary__row--total">
                        <span>{t("total")}</span>
                        <strong>
                          <CurrencyFormatter value={roundOff(totalAmount)} />
                        </strong>
                      </div>
                    </div>

                    <Button
                      type="primary"
                      block
                      className="qr-cart-place-order"
                      loading={loading}
                      disabled={loading}
                      onClick={() => {
                        if (orderSubmittingRef.current) return;

                        Number(localStorage.getItem("is_drive_thru")) === 1
                          ? setModalVisible(true)
                          : handleCheckout();
                      }}
                    >
                      {t("place_order")}
                    </Button>
                  </div>
                </footer>
              </div>
            ) : (
              // Empty cart state
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "calc(100vh - 60px)",
                }}
              >
                <div>
                  <ShoppingCartOutlined
                    style={{
                      color: "var(--color-text-muted)",
                      fontSize: "150px",
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "1.5rem",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {t("basket_empty")}
                </div>
                <Button
                  type="primary"
                  style={{
                    marginTop: "1.5rem",
                    height: "40px",
                    borderRadius: "0",
                  }}
                  onClick={() => {
                    navigate(backLink || "/");
                  }}
                >
                  {t("menu")}
                </Button>
              </div>
            )}
          </>
        ) : (
          // Order placed state
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100vh - 60px)",
            }}
          >
            <Result
              status="success"
              title={t("order_placed_successfully")}
              subTitle=" "
              extra={[
                <Button
                  type="primary"
                  style={{
                    height: "40px",
                    borderRadius: "0",
                  }}
                  onClick={() => {
                    navigate(backLink || "/");
                  }}
                  key="home"
                >
                  {t("home")}
                </Button>,
              ]}
            />
          </div>
        )}
      </div>

      {/* Additional info modal (drive-thru) */}
      <Modal
        open={modalVisible}
        title={t("additional_info")}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        footer={[
          <Button
            type="default"
            key="back"
            disabled={loading}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            loading={loading}
            disabled={loading}
            type="primary"
            onClick={handleAddEdit}
          >
            {t("place_order")}
          </Button>,
        ]}
      >
        <Form
          name="entry-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={infoForm}
        >
          <Form.Item
            name="customer_name"
            label={t("customer_name")}
            rules={[
              {
                required: true,
                message: t("validate_customer_name_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="vehicle_number"
            label={t("vehicle_number")}
            rules={[
              {
                required: true,
                message: t("validate_vehicle_number_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="vehicle_color"
            label={t("vehicle_color")}
            rules={[
              {
                required: true,
                message: t("validate_vehicle_color_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Language Drawer */}
      <Drawer
        title={t("select_language")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={() => {
          setDrawerVisible(false);
        }}
        open={drawerVisible}
        styles={{
          body: { padding: "0" },
        }}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        <Menu
          selectedKeys={[readStoredQrLocale() || "en"]}
          items={QR_LANGUAGES.map((language) => ({
            key: language.code,
            label: language.label,
            onClick: () => {
              applyQrLanguage(language.code, setAppConfigWrapper);
              setDrawerVisible(false);
            },
          }))}
        />
      </Drawer>
    </>
  );
};

export default QRcart;
